import React from 'react';
import authManager from '../../utils/auth-manager';
import startupManager from '../../utils/startup-manager';
import AppRouter from '../app-router';
import { MemoryRouter } from 'react-router';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import GenericErrorView from '../exceptions';
import NoAccessImage from '../../images/exception-screens/no-access.svg';
import Warning from '../../images/exception-screens/fatal-error.svg';

import LoadingScreen from '../loading-screen';
import '../../mocks/browser';

const queryClient = new QueryClient();

/*******************************************************************************
 * Adds a unique prefix to the domIds to create a unique id
 * @param {string} prefix A unique prefix to add to the domIds
 * @returns {domIdsStatic} If a prefix is provided, then a copy of domIdsStatic
 * is returned with the property values modified to include the prefix. If a
 * prefix is not provided a copy of domIdsStatic is returned.
 ******************************************************************************/

const startupStates = startupManager.states;

/*******************************************************************************
 * AppStartupHandler component
 * @returns A view based on the start up sequence
 ******************************************************************************/
export const AppStartupHandler = () => {
  
  const { data, isError, isLoading } = useQuery({
    queryKey: ['startup'],
    queryFn: startupManager.startUpSequence,
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isError) {
    return (
      <MemoryRouter>
        <GenericErrorView
          imgUrl={Warning}
          alt="error"
          header="There was an error during login"
          description="Please check that you are connected to the Unum network and try again."
        />
      </MemoryRouter>
    );
  }

  if (data) {
    switch (data.state) {
      case startupStates.AUTHENTICATION_ERROR:
        return (
          <MemoryRouter>
            <GenericErrorView
              imgUrl={NoAccessImage}
              alt="no access"
              header="You are not authorized to access this application"
              description="Please check that you are connected to the Unum network and ask your supervisor to check your permissions."
            />
          </MemoryRouter>
        );

      case startupStates.AUTHENTICATING:
        break;

      default:
        return (
          <AppRouter
            {...authManager.getUser()}
          />
        );
    }
  }

  return <LoadingScreen />;
};

const AppStartupHandlerWrapper = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AppStartupHandler />
    </QueryClientProvider>
  );
};

/*******************************************************************************
 * exported api definition
 ******************************************************************************/
export default AppStartupHandlerWrapper;
